import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseDto } from '@models/base/baseDto.model';
import { CreateKeyValueRequest } from '@models/base/createKeyValueRequest.model';
import { CreateResponseBase } from '@models/base/createResponseBase.model';
import { UpdateKeyValueRequest } from '@models/base/updateKeyValueRequest.model';
import { CreateEventoAgendaRequest } from '@models/eventoAgenda/createEventoAgendaRequest.model';
import { EventoAgendaDto } from '@models/eventoAgenda/eventoAgendaDto.model';
import { EventoAgendaFilter } from '@models/eventoAgenda/eventoAgendaFilter.model';
import { UpdateEventoAgendaRequest } from '@models/eventoAgenda/updateEventoAgendaRequest.model';
import { Observable, of } from 'rxjs';
import { isFalsy } from 'utility-types';

@Injectable({
  providedIn: 'root'
})
export class EventoAgendaService {

  constructor(private http: HttpClient) {
  }

  // ============================================
  // MÉTODOS CRUD BÁSICOS
  // ============================================

  /**
   * Crea un nuevo evento de agenda
   * @param createEventoAgendaRequest - Datos del evento a crear
   * @returns Observable con la respuesta de creación
   */
  create(createEventoAgendaRequest: CreateEventoAgendaRequest): Observable<CreateResponseBase> {
    return this.http.post<CreateResponseBase>("/api/EventoAgenda/Create", createEventoAgendaRequest);
  }

  /**
   * Actualiza un evento de agenda existente
   * @param updateEventoAgendaRequest - Datos del evento a actualizar
   * @returns Observable con la respuesta de actualización
   */
  update(updateEventoAgendaRequest: UpdateEventoAgendaRequest): Observable<any> {
    return this.http.put<any>("/api/EventoAgenda/Update", updateEventoAgendaRequest);
  }

  /**
   * Actualiza múltiples eventos de agenda en una sola operación
   * @param updateEventoAgendaRequests - Array de eventos a actualizar
   * @returns Observable con la respuesta de actualización
   */
  updateCollection(updateEventoAgendaRequests: UpdateEventoAgendaRequest[]): Observable<any> {
    return this.http.put<any>("/api/EventoAgenda/UpdateCollection", updateEventoAgendaRequests);
  }

  /**
   * Obtiene todos los eventos de agenda
   * @returns Observable con array de eventos
   */
  getAll(): Observable<EventoAgendaDto[]> {
    return this.http.get<EventoAgendaDto[]>("/api/EventoAgenda/GetAll");
  }

  getEventsByCustomFilterAsync(eventoAgendaFilter: EventoAgendaFilter): Observable<EventoAgendaDto[]> {
    return this.http.post<EventoAgendaDto[]>("/api/EventoAgenda/GetEventsByCustomFilterAsync", eventoAgendaFilter);
  }


  /**
   * Obtiene un evento específico por su ID
   * @param id - ID del evento a buscar
   * @returns Observable con el evento encontrado
   */
  getById(id: number): Observable<EventoAgendaDto> {
    const params = new HttpParams().set('id', id.toString());
    return this.http.get<EventoAgendaDto>("/api/EventoAgenda/GetById", { params });
  }

  /**
   * Elimina permanentemente un evento de agenda
   * @param id - ID del evento a eliminar
   * @returns Observable con la respuesta de eliminación
   */
  delete(id: number): Observable<any> {
    if (!isFalsy(id)) {
      const params = new HttpParams().set('id', id.toString());
      return this.http.delete<any>("/api/EventoAgenda/Delete", { params });
    } else {
      return of(null);
    }
  }

  /**
   * Realiza una eliminación lógica del evento (no se borra físicamente)
   * @param id - ID del evento a eliminar lógicamente
   * @returns Observable con la respuesta de eliminación
   */
  deleteLogic(id: number): Observable<any> {
    if (!isFalsy(id)) {
      const params = new HttpParams().set('id', id.toString());
      return this.http.delete<any>("/api/EventoAgenda/DeleteLogic", { params });
    } else {
      return of(null);
    }
  }

  // ============================================
  // MÉTODOS DE CONSULTA ESPECÍFICOS
  // ============================================

  /**
   * Obtiene todos los eventos asociados a un expediente específico
   * @param idExpediente - ID del expediente
   * @returns Observable con array de eventos del expediente
   */
  getAllByIdExpediente(idExpediente: number): Observable<EventoAgendaDto[]> {
    const params = new HttpParams().set('idExpediente', idExpediente.toString());
    return this.http.get<EventoAgendaDto[]>("/api/EventoAgenda/GetAllByIdExpediente", { params });
  }

  /**
   * Obtiene todos los eventos asignados a un usuario específico
   * @param userId - ID del usuario
   * @returns Observable con array de eventos del usuario
   */
  getByUserIdAsync(userId: number): Observable<EventoAgendaDto[]> {
    const params = new HttpParams().set('userId', userId.toString());
    return this.http.get<EventoAgendaDto[]>("/api/EventoAgenda/GetByUserIdAsync", { params });
  }

  /**
   * Obtiene eventos dentro de un rango de fechas específico
   * @param startDate - Fecha de inicio del rango
   * @param endDate - Fecha de fin del rango
   * @returns Observable con array de eventos en el rango
   */
  getByDateRangeAsync(startDate: Date, endDate: Date): Observable<EventoAgendaDto[]> {
    const params = new HttpParams()
      .set('startDate', startDate.toISOString())
      .set('endDate', endDate.toISOString());
    return this.http.get<EventoAgendaDto[]>("/api/EventoAgenda/GetByDateRangeAsync", { params });
  }

  /**
   * Obtiene eventos filtrados por nivel específico
   * @param status - Nivel del evento
   * @returns Observable con array de eventos con el nivel especificado
   */
  getByLevelAsync(status: number): Observable<EventoAgendaDto[]> {
    const params = new HttpParams().set('status', status.toString());
    return this.http.get<EventoAgendaDto[]>("/api/EventoAgenda/GetByLevelAsync", { params });
  }

  /**
   * Obtiene eventos filtrados por prioridad específica
   * @param priority - Nivel de prioridad del evento (ej: alta, media, baja)
   * @returns Observable con array de eventos con la prioridad especificada
   */
  getByPriorityAsync(priority: number): Observable<EventoAgendaDto[]> {
    const params = new HttpParams().set('priority', priority.toString());
    return this.http.get<EventoAgendaDto[]>("/api/EventoAgenda/GetByPriorityAsync", { params });
  }

  /**
   * Obtiene todos los eventos configurados como recurrentes
   * @returns Observable con array de eventos recurrentes
   */
  getRecurringEventsAsync(): Observable<EventoAgendaDto[]> {
    return this.http.get<EventoAgendaDto[]>("/api/EventoAgenda/GetRecurringEventsAsync");
  }

  // ============================================
  // MÉTODOS DE GESTIÓN DE EVENTOS
  // ============================================

  /**
   * Asigna un recordatorio a un evento específico
   * @param eventId - ID del evento
   * @param reminder - Tiempo del recordatorio (formato TimeSpan)
   * @returns Observable con resultado booleano de la operación
   */
  assignReminderAsync(eventId: number, reminder: string): Observable<boolean> {
    const params = new HttpParams()
      .set('eventId', eventId.toString())
      .set('reminder', reminder);
    return this.http.patch<boolean>("/api/EventoAgenda/AssignReminderAsync", null, { params });
  }

  /**
   * Actualiza la duración de un evento específico
   * @param eventId - ID del evento
   * @param duration - Nueva duración del evento (formato TimeSpan)
   * @returns Observable con resultado booleano de la operación
   */
  updateEventDurationAsync(eventId: number, duration: string): Observable<boolean> {
    const params = new HttpParams()
      .set('eventId', eventId.toString())
      .set('duration', duration);
    return this.http.patch<boolean>("/api/EventoAgenda/UpdateEventDurationAsync", null, { params });
  }

  /**
   * Cancela un evento específico
   * @param eventId - ID del evento a cancelar
   * @returns Observable con resultado booleano de la operación
   */
  cancelEventAsync(eventId: number): Observable<boolean> {
    const params = new HttpParams().set('eventId', eventId.toString());
    return this.http.patch<boolean>("/api/EventoAgenda/CancelEventAsync", null, { params });
  }

  /**
   * Marca un evento como completado
   * @param eventId - ID del evento a marcar como completado
   * @returns Observable con resultado booleano de la operación
   */
  markAsCompletedAsync(eventId: number): Observable<boolean> {
    const params = new HttpParams().set('eventId', eventId.toString());
    return this.http.patch<boolean>("/api/EventoAgenda/MarkAsCompletedAsync", null, { params });
  }

  /**
   * Agrega un comentario a un evento específico
   * @param eventId - ID del evento
   * @param comment - Comentario a agregar
   * @returns Observable con resultado booleano de la operación
   */
  addCommentToEventAsync(eventId: number, comment: string): Observable<boolean> {
    const params = new HttpParams()
      .set('eventId', eventId.toString())
      .set('comment', comment);
    return this.http.patch<boolean>("/api/EventoAgenda/AddCommentToEventAsync", null, { params });
  }

  /**
   * Actualiza la ubicación de un evento específico
   * @param eventId - ID del evento
   * @param location - Nueva ubicación del evento
   * @returns Observable con resultado booleano de la operación
   */
  updateEventLocationAsync(eventId: number, location: string): Observable<boolean> {
    const params = new HttpParams()
      .set('eventId', eventId.toString())
      .set('location', location);
    return this.http.patch<boolean>("/api/EventoAgenda/UpdateEventLocationAsync", null, { params });
  }

  /**
   * Actualiza la persona asignada a un evento específico
   * @param eventId - ID del evento
   * @param idAsignadoA - ID de la persona a asignar
   * @returns Observable con resultado booleano de la operación
   */
  updateEventAsignadoAAsync(eventId: number, idAsignadoA: number): Observable<boolean> {
    const params = new HttpParams()
      .set('eventId', eventId.toString())
      .set('idAsignadoA', idAsignadoA.toString());
    return this.http.patch<boolean>("/api/EventoAgenda/UpdateEventAsignadoAAsync", null, { params });
  }

  /**
   * Actualiza el expediente asignado a un evento específico
   * @param eventId - ID del evento
   * @param idExpediente - ID del expediente a asignar
   * @returns Observable con resultado booleano de la operación
   */
  updateEventExpedienteAsync(eventId: number, idExpediente: number): Observable<boolean> {
    const params = new HttpParams()
      .set('eventId', eventId.toString())
      .set('idExpediente', idExpediente.toString());
    return this.http.patch<boolean>("/api/EventoAgenda/UpdateEventExpedienteAsync", null, { params });
  }

  /**
   * Actualiza el título de un evento específico
   * @param eventId - ID del evento
   * @param titulo - Nuevo título del evento
   * @returns Observable con resultado booleano de la operación
   */
  updateEventTitleAsync(eventId: number, titulo: string): Observable<boolean> {
    const params = new HttpParams()
      .set('eventId', eventId.toString())
      .set('titulo', titulo);
    return this.http.patch<boolean>("/api/EventoAgenda/UpdateEventTitleAsync", null, { params });
  }

  /**
   * Actualiza las fechas de un evento específico
   * @param eventId - ID del evento
   * @param fechaInicio - Nueva fecha de inicio
   * @param fechaFin - Nueva fecha de fin
   * @returns Observable con resultado booleano de la operación
   */
  updateEventDateAsync(eventId: number, fechaInicio: Date, fechaFin: Date): Observable<boolean> {
    const params = new HttpParams()
      .set('eventId', eventId.toString())
      .set('fechaInicio', fechaInicio.toISOString())
      .set('fechaFin', fechaFin.toISOString());
    return this.http.patch<boolean>("/api/EventoAgenda/UpdateEventDateAsync", null, { params });
  }

  /**
   * Actualiza las fechas y horas de un evento específico
   * @param eventId - ID del evento
   * @param fechaInicio - Nueva fecha de inicio
   * @param fechaFin - Nueva fecha de fin
   * @param horaInicio - Nueva hora de inicio (formato TimeSpan)
   * @param horaFin - Nueva hora de fin (formato TimeSpan)
   * @returns Observable con resultado booleano de la operación
   */
  updateEventDateAndHoursAsync(eventId: number, fechaInicio: Date, fechaFin: Date, horaInicio: string, horaFin: string): Observable<boolean> {
    const params = new HttpParams()
      .set('eventId', eventId.toString())
      .set('fechaInicio', fechaInicio.toISOString())
      .set('fechaFin', fechaFin.toISOString())
      .set('horaInicio', horaInicio)
      .set('horaFin', horaFin);
    return this.http.patch<boolean>("/api/EventoAgenda/UpdateEventDateAndHoursAsync", null, { params });
  }

  /**
   * Actualiza las horas de un evento específico
   * @param eventId - ID del evento
   * @param horaInicio - Nueva hora de inicio (formato TimeSpan)
   * @param horaFin - Nueva hora de fin (formato TimeSpan)
   * @returns Observable con resultado booleano de la operación
   */
  updateEventHoursAsync(eventId: number, horaInicio: string, horaFin: string): Observable<boolean> {
    const params = new HttpParams()
      .set('eventId', eventId.toString())
      .set('horaInicio', horaInicio)
      .set('horaFin', horaFin);
    return this.http.patch<boolean>("/api/EventoAgenda/UpdateEventHoursAsync", null, { params });
  }

  /**
   * Actualiza la URL de un evento específico
   * @param eventId - ID del evento
   * @param url - Nueva URL del evento
   * @returns Observable con resultado booleano de la operación
   */
  updateEventUrlAsync(eventId: number, url: string): Observable<boolean> {
    const params = new HttpParams()
      .set('eventId', eventId.toString())
      .set('url', url);
    return this.http.patch<boolean>("/api/EventoAgenda/UpdateEventUrlAsync", null, { params });
  }

  /**
   * Actualiza el estado de un evento específico
   * @param eventId - ID del evento
   * @param idEstadoEvento - ID del nuevo estado del evento
   * @returns Observable con resultado booleano de la operación
   */
  updateEventStatusAsync(eventId: number, idEstadoEvento: number): Observable<boolean> {
    const params = new HttpParams()
      .set('eventId', eventId.toString())
      .set('idEstadoEvento', idEstadoEvento.toString());
    return this.http.patch<boolean>("/api/EventoAgenda/UpdateEventStatusAsync", null, { params });
  }

  /**
   * Actualiza el tipo de un evento específico
   * @param eventId - ID del evento
   * @param idTipoEvento - ID del nuevo tipo de evento
   * @returns Observable con resultado booleano de la operación
   */
  updateEventTypeAsync(eventId: number, idTipoEvento: number): Observable<boolean> {
    const params = new HttpParams()
      .set('eventId', eventId.toString())
      .set('idTipoEvento', idTipoEvento.toString());
    return this.http.patch<boolean>("/api/EventoAgenda/UpdateEventTypeAsync", null, { params });
  }

  // ============================================
  // MÉTODOS DE UTILIDAD PARA FECHAS
  // ============================================

  /**
   * Obtiene todos los eventos de una fecha específica
   * @param date - Fecha para buscar eventos
   * @returns Observable con array de eventos del día
   */
  getEventsByDate(date: Date): Observable<EventoAgendaDto[]> {
    const startDate = new Date(date);
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date(date);
    endDate.setHours(23, 59, 59, 999);

    return this.getByDateRangeAsync(startDate, endDate);
  }

  /**
   * Obtiene todos los eventos de la semana actual
   * @returns Observable con array de eventos de esta semana
   */
  getEventsThisWeek(): Observable<EventoAgendaDto[]> {
    const now = new Date();
    const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay()));
    startOfWeek.setHours(0, 0, 0, 0);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    return this.getByDateRangeAsync(startOfWeek, endOfWeek);
  }

  /**
   * Obtiene todos los eventos del mes actual
   * @returns Observable con array de eventos de este mes
   */
  getEventsThisMonth(): Observable<EventoAgendaDto[]> {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    endOfMonth.setHours(23, 59, 59, 999);

    return this.getByDateRangeAsync(startOfMonth, endOfMonth);
  }
}
